export interface AccrualTransaction {
  id: string;
  description: string;
  amount: number;
  type: 'receita' | 'despesa';
  category: string;
  subcategory?: string;
  due_date: Date;
  competence_date: Date;
  payment_date?: Date;
  status: AccrualStatus;
  client_name?: string;
  supplier_name?: string;
  payment_method?: string;
  document_number?: string;
  notes?: string;
  created_at: Date;
  updated_at: Date;
  is_recurring?: boolean;
  recurring_frequency?: 'monthly' | 'quarterly' | 'yearly';
  installment_number?: number;
  total_installments?: number;
}

export enum AccrualStatus {
  PENDING = 'pendente',
  PAID = 'pago',
  PARTIALLY_PAID = 'parcialmente_pago',
  OVERDUE = 'em_atraso',
  CANCELED = 'cancelado'
}

export interface AccrualPeriod {
  month: number;
  year: number;
  label: string;
  start_date: Date;
  end_date: Date;
}

export interface AccrualSummary {
  period: AccrualPeriod;
  total_revenue: number;
  total_expenses: number;
  net_result: number;
  pending_revenue: number;
  pending_expenses: number;
  paid_revenue: number;
  paid_expenses: number;
  overdue_revenue: number;
  overdue_expenses: number;
  transactions_count: number;
  payment_rate: number; // Percentage of paid transactions
}

export interface AccrualByCategory {
  category: string;
  subcategory?: string;
  total_amount: number;
  transactions_count: number;
  percentage: number;
  color?: string;
}

export interface AccrualFilters {
  period?: AccrualPeriod;
  status?: AccrualStatus[];
  type?: ('receita' | 'despesa')[];
  category?: string[];
  client_search?: string;
  amount_min?: number;
  amount_max?: number;
  sort_by?: 'competence_date' | 'due_date' | 'amount' | 'status' | 'category';
  sort_direction?: 'asc' | 'desc';
  show_recurring_only?: boolean;
  show_overdue_only?: boolean;
}

export interface AccrualGrouped {
  period: AccrualPeriod;
  transactions: AccrualTransaction[];
  summary: AccrualSummary;
  by_category: AccrualByCategory[];
  by_status: Record<AccrualStatus, { count: number; amount: number }>;
}

export interface AccrualAnalytics {
  monthly_trend: {
    month: string;
    revenue: number;
    expenses: number;
    net_result: number;
  }[];
  category_distribution: AccrualByCategory[];
  payment_performance: {
    on_time_rate: number;
    late_rate: number;
    average_delay_days: number;
  };
  recurring_vs_one_time: {
    recurring_amount: number;
    one_time_amount: number;
    recurring_percentage: number;
  };
}

export const ACCRUAL_STATUS_COLORS: Record<AccrualStatus, string> = {
  [AccrualStatus.PENDING]: 'text-yellow-600 bg-yellow-50',
  [AccrualStatus.PAID]: 'text-green-600 bg-green-50',
  [AccrualStatus.PARTIALLY_PAID]: 'text-blue-600 bg-blue-50',
  [AccrualStatus.OVERDUE]: 'text-red-600 bg-red-50',
  [AccrualStatus.CANCELED]: 'text-gray-600 bg-gray-50'
};

export const ACCRUAL_STATUS_LABELS: Record<AccrualStatus, string> = {
  [AccrualStatus.PENDING]: 'Pendente',
  [AccrualStatus.PAID]: 'Pago',
  [AccrualStatus.PARTIALLY_PAID]: 'Parcialmente Pago',
  [AccrualStatus.OVERDUE]: 'Em Atraso',
  [AccrualStatus.CANCELED]: 'Cancelado'
};

export const DEFAULT_CATEGORIES = {
  receita: [
    'Vendas',
    'Serviços',
    'Consultoria',
    'Royalties',
    'Juros',
    'Outros'
  ],
  despesa: [
    'Fornecedores',
    'Salários',
    'Impostos',
    'Aluguel',
    'Utilities',
    'Marketing',
    'Viagens',
    'Outros'
  ]
};

// Utility functions
export const getCurrentPeriod = (): AccrualPeriod => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  
  return {
    month,
    year,
    label: `${month.toString().padStart(2, '0')}/${year}`,
    start_date: new Date(year, month - 1, 1),
    end_date: new Date(year, month, 0)
  };
};

export const getPeriodFromDate = (date: Date): AccrualPeriod => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  
  return {
    month,
    year,
    label: `${month.toString().padStart(2, '0')}/${year}`,
    start_date: new Date(year, month - 1, 1),
    end_date: new Date(year, month, 0)
  };
};

export const getPeriodsInRange = (startDate: Date, endDate: Date): AccrualPeriod[] => {
  const periods: AccrualPeriod[] = [];
  const current = new Date(startDate);
  
  while (current <= endDate) {
    periods.push(getPeriodFromDate(current));
    current.setMonth(current.getMonth() + 1);
  }
  
  return periods;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(amount);
};

export const formatPeriod = (period: AccrualPeriod): string => {
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  return `${monthNames[period.month - 1]} ${period.year}`;
};

export const isTransactionInPeriod = (transaction: AccrualTransaction, period: AccrualPeriod): boolean => {
  const competenceDate = new Date(transaction.competence_date);
  return competenceDate >= period.start_date && competenceDate <= period.end_date;
};

export const calculatePaymentRate = (transactions: AccrualTransaction[]): number => {
  if (transactions.length === 0) return 0;
  
  const paidTransactions = transactions.filter(t => 
    t.status === AccrualStatus.PAID || t.status === AccrualStatus.PARTIALLY_PAID
  );
  
  return (paidTransactions.length / transactions.length) * 100;
};